import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Download,
  FileText,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowRight,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  BarChart3,
  Target,
  Globe,
  Landmark,
  Settings,
  FileDown,
  Snowflake,
  RefreshCcw } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart,
  ComposedChart } from
'recharts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const FinancialReports = () => {const { t } = useTranslation("finance");
  const [period, setPeriod] = useState('monthly');
  const [cashFlowPeriod, setCashFlowPeriod] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [profitLoss, setProfitLoss] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [revenueByMonth, setRevenueByMonth] = useState([]);

  // New Cash Flow states
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categoryDetails, setCategoryDetails] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [forecast, setForecast] = useState(null);
  const [budgetComparison, setBudgetComparison] = useState(null);
  const [bankReconciliation, setBankReconciliation] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [currencyRates, setCurrencyRates] = useState(null);
  const [showForecastModal, setShowForecastModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [useSeasonalForecast, setUseSeasonalForecast] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const currencies = ['USD', 'EUR', 'GBP', 'BRL', 'CAD', 'JPY', 'AUD', 'CHF', 'CNY', 'MXN'];

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateParams = customStartDate && customEndDate ?
      `&start_date=${customStartDate}&end_date=${customEndDate}` :
      '';

      const [plRes, bsRes, cfRes, trendRes, ratesRes] = await Promise.all([
      fetch(`${API_URL}/api/finance/reports/profit-loss`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/reports/balance-sheet`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/reports/cash-flow?period=${cashFlowPeriod}${dateParams}`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/reports/revenue-trend`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/currency/rates?base_currency=USD`, { headers: getAuthHeaders() })]
      );
      if (!plRes.ok || !bsRes.ok || !cfRes.ok || !trendRes.ok) throw new Error('Failed to fetch reports');
      const [plData, bsData, cfData, trendData, ratesData] = await Promise.all([
      plRes.json(), bsRes.json(), cfRes.json(), trendRes.json(), ratesRes.ok ? ratesRes.json() : null]
      );
      setProfitLoss(plData);
      setBalanceSheet(bsData);
      setCashFlow(cfData);
      setRevenueByMonth(Array.isArray(trendData) ? trendData : trendData.trend || []);
      if (ratesData) setCurrencyRates(ratesData);
    } catch (err) {
      toast.error('Failed to load financial reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryDetail = async (category, entryType) => {
    const key = `${entryType}-${category}`;
    if (categoryDetails[key]) return; // Already loaded

    try {
      const dateParams = customStartDate && customEndDate ?
      `&start_date=${customStartDate}&end_date=${customEndDate}` :
      '';
      const res = await fetch(
        `${API_URL}/api/finance/reports/cash-flow/detail/${encodeURIComponent(category)}?entry_type=${entryType}&period=${cashFlowPeriod}${dateParams}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setCategoryDetails((prev) => ({ ...prev, [key]: data }));
      }
    } catch (err) {
      console.error('Failed to fetch category detail:', err);
    }
  };

  const toggleCategory = async (category, entryType) => {
    const key = `${entryType}-${category}`;
    const isExpanded = expandedCategories[key];

    if (!isExpanded) {
      await fetchCategoryDetail(category, entryType);
    }

    setExpandedCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchForecast = async () => {
    try {
      const endpoint = useSeasonalForecast ?
      `${API_URL}/api/finance/reports/cash-flow/forecast-seasonal?months=12` :
      `${API_URL}/api/finance/reports/cash-flow/forecast?months=6`;
      const res = await fetch(endpoint, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setForecast(data);
        setShowForecastModal(true);
      }
    } catch (err) {
      toast.error('Failed to load forecast');
    }
  };

  const fetchBudgetComparison = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/reports/cash-flow/budget-comparison?period=${cashFlowPeriod}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBudgetComparison(data);
        setShowBudgetModal(true);
      }
    } catch (err) {
      toast.error('Failed to load budget comparison');
    }
  };

  const fetchBankReconciliation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/bank-reconciliation`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBankReconciliation(data);
        setShowReconciliationModal(true);
      }
    } catch (err) {
      toast.error('Failed to load bank reconciliation');
    }
  };

  const exportCashFlow = async (format) => {
    setExporting(true);
    try {
      const dateParams = customStartDate && customEndDate ?
      `&start_date=${customStartDate}&end_date=${customEndDate}` :
      '';

      if (format === 'pdf') {
        // PDF export
        const res = await fetch(
          `${API_URL}/api/finance/reports/cash-flow/export-pdf?period=${cashFlowPeriod}${dateParams}`,
          { headers: getAuthHeaders() }
        );
        if (res.ok) {
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `cash_flow_${cashFlowPeriod}_${new Date().toISOString().split('T')[0]}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.success('PDF exported successfully');
        }
      } else {
        const res = await fetch(
          `${API_URL}/api/finance/reports/cash-flow/export?format=${format}&period=${cashFlowPeriod}${dateParams}`,
          { headers: getAuthHeaders() }
        );

        if (res.ok) {
          if (format === 'csv') {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cash_flow_${cashFlowPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('CSV exported successfully');
          } else {
            const data = await res.json();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cash_flow_${cashFlowPeriod}_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('JSON exported successfully');
          }
        }
      }
    } catch (err) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const fetchSeasonalForecast = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/reports/cash-flow/forecast-seasonal?months=12`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setForecast(data);
        setShowForecastModal(true);
      }
    } catch (err) {
      toast.error('Failed to load seasonal forecast');
    }
  };

  const fetchLiveCurrencyRates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/currency/rates-live?base_currency=USD`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCurrencyRates(data);
        toast.success(`Currency rates updated (${data.source})`);
      }
    } catch (err) {
      toast.error('Failed to fetch live rates');
    }
  };

  const applyCustomDateRange = () => {
    setShowDatePicker(false);
    fetchData();
  };

  const convertAmount = (amount) => {
    if (!amount || selectedCurrency === 'USD' || !currencyRates?.rates) return amount;
    const rate = currencyRates.rates[selectedCurrency] || 1;
    return amount * rate;
  };

  const formatCurrency = (amount) => {
    const converted = convertAmount(amount);
    const symbol = selectedCurrency === 'USD' ? '$' : selectedCurrency === 'EUR' ? '€' : selectedCurrency === 'GBP' ? '£' : selectedCurrency === 'BRL' ? 'R$' : '$';
    return `${symbol}${(converted || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  useEffect(() => {
    fetchData();
  }, [cashFlowPeriod]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  const pl = profitLoss || {};
  const bs = balanceSheet || {};
  const cf = cashFlow || {};
  const revenue = Array.isArray(revenueByMonth) ? revenueByMonth : [];

  return (
    <div className="space-y-6" data-testid="finance-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("financialReports", "Financial Reports")}</h1>
          <p className="text-muted-foreground mt-1">{t("profitLossBalance", "Profit & Loss, Balance Sheet, Cash Flow statements")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <select
            className="h-10 px-3 border border-input rounded-md bg-background"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}>
            
            <option value="monthly">{t("monthly", "Monthly")}</option>
            <option value="quarterly">{t("quarterly", "Quarterly")}</option>
            <option value="yearly">{t("yearly", "Yearly")}</option>
          </select>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("exportPdf", "Export PDF")}

          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pl" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="pl" className="gap-2">
            <TrendingUp className="h-4 w-4" />{t("profitLoss", "Profit & Loss")}

          </TabsTrigger>
          <TabsTrigger value="bs" className="gap-2">
            <FileText className="h-4 w-4" />{t("balanceSheet", "Balance Sheet")}

          </TabsTrigger>
          <TabsTrigger value="cf" className="gap-2">
            <DollarSign className="h-4 w-4" />{t("cashFlow", "Cash Flow")}

          </TabsTrigger>
        </TabsList>

        {/* P&L Tab */}
        <TabsContent value="pl" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-4">
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("totalRevenue", "Total Revenue")}</p>
                <p className="text-2xl font-bold text-emerald-600">${((pl.revenue || pl.totals?.total_revenue || 0) / 1000).toFixed(1)}k</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("totalExpenses", "Total Expenses")}</p>
                <p className="text-2xl font-bold text-red-600">${((pl.expenses || pl.totals?.total_expenses || 0) / 1000).toFixed(1)}k</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("grossProfit", "Gross Profit")}</p>
                <p className="text-2xl font-bold text-emerald-600">${((pl.gross_profit || pl.totals?.gross_profit || 0) / 1000).toFixed(1)}k</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("netIncome", "Net Income")}</p>
                <p className="text-2xl font-bold text-purple-600">${((pl.net_income || pl.totals?.net_income || 0) / 1000).toFixed(1)}k</p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Trend Chart */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("revenueVsExpenses", "Revenue vs Expenses Trend")}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => `$${value.toLocaleString()}`} />
                  
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* P&L Detail Tables */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("revenue", "Revenue")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-2 px-4 text-sm">{t("category", "Category")}</th>
                      <th className="text-right py-2 px-4 text-sm">{t("actual", "Actual")}</th>
                      <th className="text-right py-2 px-4 text-sm">{t("budget", "Budget")}</th>
                      <th className="text-right py-2 px-4 text-sm">{t("var", "Var %")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(pl.revenue) ? pl.revenue : [
                    { category: 'Product Sales', amount: (pl.revenue || 0) * 0.5, budget: (pl.revenue || 0) * 0.48, variance: 4.2 },
                    { category: 'Service Revenue', amount: (pl.revenue || 0) * 0.3, budget: (pl.revenue || 0) * 0.32, variance: -6.3 },
                    { category: 'Other Income', amount: (pl.revenue || 0) * 0.2, budget: (pl.revenue || 0) * 0.2, variance: 0.0 }]).
                    map((item, idx) =>
                    <tr key={idx} className="border-b border-border/50">
                        <td className="py-2 px-4 text-sm">{item.category}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono">${item.amount?.toLocaleString()}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono text-muted-foreground">${item.budget?.toLocaleString()}</td>
                        <td className={`py-2 px-4 text-sm text-right ${item.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {item.variance > 0 ? '+' : ''}{item.variance}%
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("expenses", "Expenses")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-2 px-4 text-sm">{t("category", "Category")}</th>
                      <th className="text-right py-2 px-4 text-sm">{t("actual", "Actual")}</th>
                      <th className="text-right py-2 px-4 text-sm">{t("budget", "Budget")}</th>
                      <th className="text-right py-2 px-4 text-sm">{t("var", "Var %")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(pl.expenses) ? pl.expenses : [
                    { category: 'Cost of Goods', amount: (pl.expenses || 0) * 0.4, budget: (pl.expenses || 0) * 0.42, variance: -4.8 },
                    { category: 'Salaries', amount: (pl.expenses || 0) * 0.35, budget: (pl.expenses || 0) * 0.33, variance: 6.1 },
                    { category: 'Operations', amount: (pl.expenses || 0) * 0.15, budget: (pl.expenses || 0) * 0.15, variance: 0.0 },
                    { category: 'Marketing', amount: (pl.expenses || 0) * 0.1, budget: (pl.expenses || 0) * 0.10, variance: 0.0 }]).
                    map((item, idx) =>
                    <tr key={idx} className="border-b border-border/50">
                        <td className="py-2 px-4 text-sm">{item.category}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono">${item.amount?.toLocaleString()}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono text-muted-foreground">${item.budget?.toLocaleString()}</td>
                        <td className={`py-2 px-4 text-sm text-right ${item.variance <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {item.variance > 0 ? '+' : ''}{item.variance}%
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="bs" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("totalAssets", "Total Assets")}</p>
                <p className="text-2xl font-bold text-emerald-600">${(bs.totals?.total_assets / 1000000 || 0).toFixed(2)}M</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("totalLiabilities", "Total Liabilities")}</p>
                <p className="text-2xl font-bold text-red-600">${(bs.totals?.total_liabilities / 1000 || 0).toFixed(0)}k</p>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t("totalEquity", "Total Equity")}</p>
                <p className="text-2xl font-bold text-blue-600">${(bs.totals?.total_equity / 1000000 || 0).toFixed(2)}M</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("assets", "Assets")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b bg-muted/50">
                      <td className="py-2 px-4 text-sm font-medium" colSpan={2}>{t("currentAssets", "Current Assets")}</td>
                    </tr>
                    {(bs.assets?.current || [
                    { account: 'Cash & Equivalents', amount: (bs.assets || 0) * 0.25 },
                    { account: 'Accounts Receivable', amount: (bs.assets || 0) * 0.35 },
                    { account: 'Inventory', amount: (bs.assets || 0) * 0.15 }]).
                    map((item, idx) =>
                    <tr key={idx} className="border-b border-border/50">
                        <td className="py-2 px-4 text-sm pl-8">{item.account}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono">${item.amount?.toLocaleString()}</td>
                      </tr>
                    )}
                    <tr className="border-b bg-muted/50">
                      <td className="py-2 px-4 text-sm font-medium" colSpan={2}>{t("noncurrentAssets", "Non-Current Assets")}</td>
                    </tr>
                    {(bs.assets?.non_current || [
                    { account: 'Property & Equipment', amount: (bs.assets || 0) * 0.20 },
                    { account: 'Intangible Assets', amount: (bs.assets || 0) * 0.05 }]).
                    map((item, idx) =>
                    <tr key={idx} className="border-b border-border/50">
                        <td className="py-2 px-4 text-sm pl-8">{item.account}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono">${item.amount?.toLocaleString()}</td>
                      </tr>
                    )}
                    <tr className="bg-emerald-50 font-semibold">
                      <td className="py-2 px-4 text-sm">{t("totalAssets", "Total Assets")}</td>
                      <td className="py-2 px-4 text-sm text-right font-mono text-emerald-700">
                        ${(bs.assets || bs.totals?.total_assets || 0)?.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">{t("liabilitiesEquity", "Liabilities & Equity")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b bg-muted/50">
                      <td className="py-2 px-4 text-sm font-medium" colSpan={2}>{t("currentLiabilities", "Current Liabilities")}</td>
                    </tr>
                    {(bs.liabilities?.current || [
                    { account: 'Accounts Payable', amount: (bs.liabilities || 0) * 0.5 },
                    { account: 'Short-term Debt', amount: (bs.liabilities || 0) * 0.3 },
                    { account: 'Accrued Expenses', amount: (bs.liabilities || 0) * 0.2 }]).
                    map((item, idx) =>
                    <tr key={idx} className="border-b border-border/50">
                        <td className="py-2 px-4 text-sm pl-8">{item.account}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono">${item.amount?.toLocaleString()}</td>
                      </tr>
                    )}
                    <tr className="border-b bg-muted/50">
                      <td className="py-2 px-4 text-sm font-medium" colSpan={2}>{t("equity", "Equity")}</td>
                    </tr>
                    {(Array.isArray(bs.equity) ? bs.equity : [
                    { account: 'Common Stock', amount: (bs.equity || 0) * 0.2 },
                    { account: 'Retained Earnings', amount: (bs.equity || 0) * 0.8 }]).
                    map((item, idx) =>
                    <tr key={idx} className="border-b border-border/50">
                        <td className="py-2 px-4 text-sm pl-8">{item.account}</td>
                        <td className="py-2 px-4 text-sm text-right font-mono">${item.amount?.toLocaleString()}</td>
                      </tr>
                    )}
                    <tr className="bg-blue-50 font-semibold">
                      <td className="py-2 px-4 text-sm">{t("totalLiabilitiesEquity", "Total Liabilities & Equity")}</td>
                      <td className="py-2 px-4 text-sm text-right font-mono text-blue-700">
                        ${(bs.total || (bs.liabilities || 0) + (bs.equity || 0))?.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cf" className="space-y-4">
          {/* Enhanced Controls Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Period Selector */}
              <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                {['weekly', 'monthly', 'quarterly', 'yearly'].map((p) =>
                <button
                  key={p}
                  onClick={() => setCashFlowPeriod(p)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  cashFlowPeriod === p ?
                  'bg-white shadow text-emerald-600' :
                  'text-muted-foreground hover:text-foreground'}`
                  }>
                  
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                )}
                <button
                  onClick={() => setShowDatePicker(true)}
                  className="px-3 py-1.5 text-sm font-medium rounded-md text-muted-foreground hover:text-foreground flex items-center gap-1">
                  
                  <Calendar className="h-3 w-3" />{t("custom", "Custom")}

                </button>
              </div>
              
              {/* Currency Selector */}
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger className="w-24 h-8">
                  <Globe className="h-3 w-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) =>
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {/* Forecast Controls */}
              <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                <Button
                  variant={useSeasonalForecast ? "ghost" : "secondary"}
                  size="sm"
                  onClick={() => setUseSeasonalForecast(false)}
                  className="gap-1 h-7 text-xs">
                  
                  <BarChart3 className="h-3 w-3" />{t("linear", "Linear")}

                </Button>
                <Button
                  variant={useSeasonalForecast ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setUseSeasonalForecast(true)}
                  className="gap-1 h-7 text-xs">
                  
                  <Snowflake className="h-3 w-3" />{t("seasonal", "Seasonal")}

                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={fetchForecast} className="gap-1">
                <BarChart3 className="h-4 w-4" />{t("forecast", "Forecast")}

              </Button>
              <Button variant="outline" size="sm" onClick={fetchBudgetComparison} className="gap-1">
                <Target className="h-4 w-4" />{t("budget", "Budget")}

              </Button>
              <Button variant="outline" size="sm" onClick={fetchBankReconciliation} className="gap-1">
                <Landmark className="h-4 w-4" />{t("reconcile", "Reconcile")}

              </Button>
              <div className="border-l h-6 mx-1" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportCashFlow('pdf')}
                disabled={exporting}
                className="gap-1 bg-red-50 hover:bg-red-100 border-red-200"
                data-testid="export-pdf-btn">
                
                <FileDown className="h-4 w-4 text-red-600" />{t("pdf", "PDF")}

              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportCashFlow('csv')}
                disabled={exporting}
                className="gap-1">
                
                <FileSpreadsheet className="h-4 w-4" />{t("csv", "CSV")}

              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportCashFlow('json')}
                disabled={exporting}
                className="gap-1">
                
                <Download className="h-4 w-4" />{t("json", "JSON")}

              </Button>
            </div>
          </div>

          {/* Period Label */}
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-muted-foreground">
              <Calendar className="h-3 w-3 mr-1" />
              {cf.period_label || 'Current Period'}
              {selectedCurrency !== 'USD' && ` (${selectedCurrency})`}
            </Badge>
            {customStartDate && customEndDate &&
            <Badge variant="secondary" className="gap-1">{t("custom", "Custom:")}
              {customStartDate}{t("to", "to")}{customEndDate}
                <button onClick={() => {setCustomStartDate('');setCustomEndDate('');fetchData();}} className="ml-1 text-xs hover:text-red-500">×</button>
              </Badge>
            }
          </div>

          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-4">
            <Card className="card-elevated border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("totalRevenue", "Total Revenue")}</p>
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                </div>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(cf.totals?.total_revenue || cf.totals?.operating_total * 1.5 || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-elevated border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("totalExpenses", "Total Expenses")}</p>
                  <TrendingDown className="h-4 w-4 text-red-500" />
                </div>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(cf.totals?.total_expenses || Math.abs(cf.totals?.investing_total || 0) * 2 || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-elevated border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("operating", "Operating")}</p>
                  <DollarSign className="h-4 w-4 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(cf.totals?.operating_total || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-elevated border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("netCashFlow", "Net Cash Flow")}</p>
                  <ArrowRight className="h-4 w-4 text-purple-500" />
                </div>
                <p className={`text-2xl font-bold ${(cf.totals?.net_change || cf.totals?.net_cash_flow || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(cf.totals?.net_change || cf.totals?.net_cash_flow || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue & Expenses Tables with Expandable Categories */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Revenue Table */}
            <Card className="card-elevated">
              <CardHeader className="pb-3 border-b bg-emerald-50/50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />{t("revenueByCategory", "Revenue by Category")}

                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {(cf.revenue || [
                  { category: "Product Sales", amount: (cf.totals?.operating_total || 0) * 0.5, entries: [] },
                  { category: "Service Revenue", amount: (cf.totals?.operating_total || 0) * 0.35, entries: [] },
                  { category: "Other Revenue", amount: (cf.totals?.operating_total || 0) * 0.15, entries: [] }]).
                  map((item, idx) => {
                    const totalRev = cf.totals?.total_revenue || cf.totals?.operating_total * 1.5 || 1;
                    const percentage = ((item.amount || 0) / totalRev * 100).toFixed(1);
                    const key = `revenue-${item.category}`;
                    const isExpanded = expandedCategories[key];
                    const detail = categoryDetails[key];

                    return (
                      <div key={idx}>
                        <button
                          onClick={() => toggleCategory(item.category, 'revenue')}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                          
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-sm font-medium">{item.category}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-mono text-emerald-600">{formatCurrency(item.amount || 0)}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(percentage, 100)}%` }} />
                              </div>
                              <span className="text-xs text-muted-foreground w-10">{percentage}%</span>
                            </div>
                          </div>
                        </button>
                        
                        {isExpanded &&
                        <div className="bg-muted/20 border-t">
                            {detail ?
                          <div className="p-3 space-y-2">
                                {detail.subcategories?.map((sub, sIdx) =>
                            <div key={sIdx} className="pl-6">
                                    <div className="flex justify-between items-center py-1">
                                      <span className="text-sm text-muted-foreground">{sub.name}</span>
                                      <span className="text-sm font-mono">{formatCurrency(sub.amount)}</span>
                                    </div>
                                    {sub.entries?.slice(0, 3).map((entry, eIdx) =>
                              <div key={eIdx} className="pl-4 flex justify-between items-center py-0.5 text-xs text-muted-foreground">
                                        <span className="truncate max-w-[200px]">{entry.description}</span>
                                        <span className="font-mono">{formatCurrency(entry.amount)}</span>
                                      </div>
                              )}
                                    {(sub.entries?.length || 0) > 3 &&
                              <div className="pl-4 text-xs text-muted-foreground">+{sub.entries.length - 3}{t("moreEntries", "more entries")}</div>
                              }
                                  </div>
                            )}
                                <div className="pt-2 pl-6 text-xs text-muted-foreground">{t("total", "Total:")}
                              {detail.entry_count}{t("entries", "entries")}
                            </div>
                              </div> :

                          <div className="p-4 text-center text-sm text-muted-foreground">{t("loading", "Loading...")}</div>
                          }
                          </div>
                        }
                      </div>);

                  })}
                  <div className="px-4 py-3 bg-emerald-50 font-semibold flex justify-between">
                    <span>{t("totalRevenue", "Total Revenue")}</span>
                    <span className="font-mono text-emerald-700">{formatCurrency(cf.totals?.total_revenue || cf.totals?.operating_total * 1.5 || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expenses Table */}
            <Card className="card-elevated">
              <CardHeader className="pb-3 border-b bg-red-50/50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-600" />{t("expensesByCategory", "Expenses by Category")}

                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {(cf.expenses || [
                  { category: "Payroll", amount: (cf.totals?.total_expenses || 0) * 0.4, entries: [] },
                  { category: "Operations", amount: (cf.totals?.total_expenses || 0) * 0.25, entries: [] },
                  { category: "Marketing", amount: (cf.totals?.total_expenses || 0) * 0.15, entries: [] },
                  { category: "Technology", amount: (cf.totals?.total_expenses || 0) * 0.2, entries: [] }]).
                  map((item, idx) => {
                    const totalExp = cf.totals?.total_expenses || Math.abs(cf.totals?.investing_total || 0) * 2 || 1;
                    const percentage = ((item.amount || 0) / totalExp * 100).toFixed(1);
                    const key = `expense-${item.category}`;
                    const isExpanded = expandedCategories[key];
                    const detail = categoryDetails[key];

                    return (
                      <div key={idx}>
                        <button
                          onClick={() => toggleCategory(item.category, 'expense')}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                          
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                            <div className="w-2 h-2 rounded-full bg-red-500" />
                            <span className="text-sm font-medium">{item.category}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-mono text-red-600">{formatCurrency(item.amount || 0)}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.min(percentage, 100)}%` }} />
                              </div>
                              <span className="text-xs text-muted-foreground w-10">{percentage}%</span>
                            </div>
                          </div>
                        </button>
                        
                        {isExpanded &&
                        <div className="bg-muted/20 border-t">
                            {detail ?
                          <div className="p-3 space-y-2">
                                {detail.subcategories?.map((sub, sIdx) =>
                            <div key={sIdx} className="pl-6">
                                    <div className="flex justify-between items-center py-1">
                                      <span className="text-sm text-muted-foreground">{sub.name}</span>
                                      <span className="text-sm font-mono">{formatCurrency(sub.amount)}</span>
                                    </div>
                                    {sub.entries?.slice(0, 3).map((entry, eIdx) =>
                              <div key={eIdx} className="pl-4 flex justify-between items-center py-0.5 text-xs text-muted-foreground">
                                        <span className="truncate max-w-[200px]">{entry.description}</span>
                                        <span className="font-mono">{formatCurrency(entry.amount)}</span>
                                      </div>
                              )}
                                    {(sub.entries?.length || 0) > 3 &&
                              <div className="pl-4 text-xs text-muted-foreground">+{sub.entries.length - 3}{t("moreEntries", "more entries")}</div>
                              }
                                  </div>
                            )}
                                <div className="pt-2 pl-6 text-xs text-muted-foreground">{t("total", "Total:")}
                              {detail.entry_count}{t("entries", "entries")}
                            </div>
                              </div> :

                          <div className="p-4 text-center text-sm text-muted-foreground">{t("loading", "Loading...")}</div>
                          }
                          </div>
                        }
                      </div>);

                  })}
                  <div className="px-4 py-3 bg-red-50 font-semibold flex justify-between">
                    <span>{t("totalExpenses", "Total Expenses")}</span>
                    <span className="font-mono text-red-700">{formatCurrency(cf.totals?.total_expenses || Math.abs(cf.totals?.investing_total || 0) * 2 || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cash Flow Statement by Activity */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />{t("cashFlowStatement", "Cash Flow Statement by Activity Type")}

              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <tbody>
                  <tr className="border-b bg-blue-50">
                    <td className="py-3 px-4 text-sm font-semibold text-blue-800" colSpan={2}>{t("operatingActivities", "Operating Activities")}

                    </td>
                  </tr>
                  {(Array.isArray(cf.operating) ? cf.operating : cf.operating?.items || []).map((item, idx) =>
                  <tr key={idx} className="border-b border-border/50">
                      <td className="py-2 px-4 text-sm pl-8">{item.item}</td>
                      <td className={`py-2 px-4 text-sm text-right font-mono ${item.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {item.amount >= 0 ? '+' : ''}{formatCurrency(item.amount)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b bg-muted/30">
                    <td className="py-2 px-4 text-sm pl-8 font-medium">{t("netFromOperating", "Net from Operating")}</td>
                    <td className={`py-2 px-4 text-sm text-right font-mono font-medium ${cf.totals?.operating_total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(cf.totals?.operating_total || 0)}
                    </td>
                  </tr>
                  
                  <tr className="border-b bg-amber-50">
                    <td className="py-3 px-4 text-sm font-semibold text-amber-800" colSpan={2}>{t("investingActivities", "Investing Activities")}

                    </td>
                  </tr>
                  {(Array.isArray(cf.investing) ? cf.investing : cf.investing?.items || []).map((item, idx) =>
                  <tr key={idx} className="border-b border-border/50">
                      <td className="py-2 px-4 text-sm pl-8">{item.item}</td>
                      <td className={`py-2 px-4 text-sm text-right font-mono ${item.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {item.amount >= 0 ? '+' : ''}{formatCurrency(item.amount)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b bg-muted/30">
                    <td className="py-2 px-4 text-sm pl-8 font-medium">{t("netFromInvesting", "Net from Investing")}</td>
                    <td className={`py-2 px-4 text-sm text-right font-mono font-medium ${cf.totals?.investing_total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(cf.totals?.investing_total || 0)}
                    </td>
                  </tr>
                  
                  <tr className="border-b bg-purple-50">
                    <td className="py-3 px-4 text-sm font-semibold text-purple-800" colSpan={2}>{t("financingActivities", "Financing Activities")}

                    </td>
                  </tr>
                  {(Array.isArray(cf.financing) ? cf.financing : cf.financing?.items || []).map((item, idx) =>
                  <tr key={idx} className="border-b border-border/50">
                      <td className="py-2 px-4 text-sm pl-8">{item.item}</td>
                      <td className={`py-2 px-4 text-sm text-right font-mono ${item.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {item.amount >= 0 ? '+' : ''}{formatCurrency(item.amount)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b bg-muted/30">
                    <td className="py-2 px-4 text-sm pl-8 font-medium">{t("netFromFinancing", "Net from Financing")}</td>
                    <td className={`py-2 px-4 text-sm text-right font-mono font-medium ${cf.totals?.financing_total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(cf.totals?.financing_total || 0)}
                    </td>
                  </tr>
                  
                  <tr className="bg-gradient-to-r from-emerald-100 to-blue-100 font-bold">
                    <td className="py-4 px-4 text-base">{t("netChangeIn", "Net Change in Cash")}</td>
                    <td className={`py-4 px-4 text-right font-mono text-lg ${(cf.totals?.net_change || cf.totals?.net_cash_flow || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatCurrency(cf.totals?.net_change || cf.totals?.net_cash_flow || 0)}
                    </td>
                  </tr>
                  <tr className="bg-muted/50">
                    <td className="py-3 px-4 text-sm font-medium">{t("endingCashBalance", "Ending Cash Balance")}</td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600 text-lg">
                      {formatCurrency(cf.totals?.ending_cash || cf.totals?.net_change || cf.totals?.net_cash_flow || 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Date Picker Modal */}
      <Dialog open={showDatePicker} onOpenChange={setShowDatePicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("customDateRange", "Custom Date Range")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t("startDate", "Start Date")}</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md" />
              
            </div>
            <div>
              <label className="text-sm font-medium">{t("endDate", "End Date")}</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md" />
              
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDatePicker(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={applyCustomDateRange}>{t("apply", "Apply")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forecast Modal */}
      <Dialog open={showForecastModal} onOpenChange={setShowForecastModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {forecast?.method === 'seasonal_decomposition' ?
              <Snowflake className="h-5 w-5 text-blue-600" /> :

              <BarChart3 className="h-5 w-5 text-emerald-600" />
              }{t("cashFlowForecast", "Cash Flow Forecast (")}
              {forecast?.forecast?.length || 6}{t("months", "Months)")}
              {forecast?.method === 'seasonal_decomposition' &&
              <Badge className="ml-2 bg-blue-100 text-blue-700">{t("seasonal", "Seasonal")}</Badge>
              }
            </DialogTitle>
          </DialogHeader>
          {forecast &&
          <div className="space-y-6">
              {/* Method Description */}
              {forecast.method &&
            <div className="bg-muted/50 p-3 rounded-lg text-sm text-muted-foreground">
                  <strong>{t("method", "Method:")}</strong> {forecast.method === 'seasonal_decomposition' ?
              'Holt-Winters-like decomposition with trend + seasonal components' :
              'Linear regression trend analysis'}
                  {forecast.seasonal_indices &&
              <span className="ml-2">{t("peakMonth", "\u2022 Peak month:")}
                {forecast.seasonal_indices?.revenue ?
                `Month ${Object.entries(forecast.seasonal_indices.revenue).sort((a, b) => b[1] - a[1])[0]?.[0]}` : 'N/A'}
                    </span>
              }
                </div>
            }
              
              {/* Forecast Chart */}
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={[...(forecast.historical || []), ...(forecast.forecast || []).map((f) => ({ ...f, isForecast: true }))]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `$${v?.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#10b981" />
                    <Bar dataKey="expenses" name="Expenses" fill="#ef4444" />
                    <Line type="monotone" dataKey="net" name="Net Cash Flow" stroke="#8b5cf6" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Forecast Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t("avgForecastRevenue", "Avg Forecast Revenue")}</p>
                    <p className="text-xl font-bold text-emerald-600">${(forecast.summary?.avg_forecast_revenue || 0).toLocaleString()}</p>
                    <Badge className={forecast.summary?.revenue_trend === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {forecast.summary?.revenue_trend === 'up' ? '↑ Trending Up' : '↓ Trending Down'}
                    </Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t("avgForecastExpenses", "Avg Forecast Expenses")}</p>
                    <p className="text-xl font-bold text-red-600">${(forecast.summary?.avg_forecast_expenses || 0).toLocaleString()}</p>
                    <Badge className={forecast.summary?.expense_trend === 'up' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                      {forecast.summary?.expense_trend === 'up' ? '↑ Trending Up' : '↓ Trending Down'}
                    </Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{t("totalForecastNet", "Total Forecast Net")}</p>
                    <p className={`text-xl font-bold ${(forecast.summary?.total_forecast_net || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${(forecast.summary?.total_forecast_net || 0).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Forecast Table */}
              <table className="w-full border">
                <thead className="bg-muted">
                  <tr>
                    <th className="py-2 px-3 text-left text-sm">{t("month", "Month")}</th>
                    <th className="py-2 px-3 text-right text-sm">{t("revenue", "Revenue")}</th>
                    <th className="py-2 px-3 text-right text-sm">{t("expenses", "Expenses")}</th>
                    <th className="py-2 px-3 text-right text-sm">{t("net", "Net")}</th>
                    <th className="py-2 px-3 text-right text-sm">{t("confidence", "Confidence")}</th>
                    {forecast.method === 'seasonal_decomposition' &&
                  <th className="py-2 px-3 text-right text-sm">{t("seasonalFactor", "Seasonal Factor")}</th>
                  }
                  </tr>
                </thead>
                <tbody>
                  {forecast.forecast?.map((f, idx) =>
                <tr key={idx} className="border-t">
                      <td className="py-2 px-3 text-sm">{f.month}</td>
                      <td className="py-2 px-3 text-right text-sm text-emerald-600">${f.revenue?.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right text-sm text-red-600">${f.expenses?.toLocaleString()}</td>
                      <td className={`py-2 px-3 text-right text-sm font-medium ${f.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ${f.net?.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right text-sm">
                        <Badge variant="outline">{((f.confidence || 0) * 100).toFixed(0)}%</Badge>
                      </td>
                      {forecast.method === 'seasonal_decomposition' &&
                  <td className="py-2 px-3 text-right text-sm">
                          <span className={f.seasonal_factor_revenue > 1 ? 'text-emerald-600' : 'text-amber-600'}>
                            ×{(f.seasonal_factor_revenue || 1).toFixed(2)}
                          </span>
                        </td>
                  }
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForecastModal(false)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Comparison Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />{t("budgetVsActual", "Budget vs Actual Comparison")}

            </DialogTitle>
          </DialogHeader>
          {budgetComparison &&
          <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("actualRevenue", "Actual Revenue")}</p>
                    <p className="text-lg font-bold text-emerald-600">${(budgetComparison.totals?.actual_revenue || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("budgetRevenue", "Budget Revenue")}</p>
                    <p className="text-lg font-bold text-blue-600">${(budgetComparison.totals?.budget_revenue || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("actualExpenses", "Actual Expenses")}</p>
                    <p className="text-lg font-bold text-red-600">${(budgetComparison.totals?.actual_expenses || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("budgetExpenses", "Budget Expenses")}</p>
                    <p className="text-lg font-bold text-amber-600">${(budgetComparison.totals?.budget_expenses || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Budget Chart */}
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={budgetComparison.categories?.slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `$${v?.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="actual_revenue" name="Actual Rev" fill="#10b981" />
                    <Bar dataKey="budget_revenue" name="Budget Rev" fill="#60a5fa" />
                    <Bar dataKey="actual_expenses" name="Actual Exp" fill="#ef4444" />
                    <Bar dataKey="budget_expenses" name="Budget Exp" fill="#fbbf24" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Detailed Table */}
              <table className="w-full border text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="py-2 px-3 text-left">{t("category", "Category")}</th>
                    <th className="py-2 px-3 text-right">{t("actRevenue", "Act. Revenue")}</th>
                    <th className="py-2 px-3 text-right">{t("budRevenue", "Bud. Revenue")}</th>
                    <th className="py-2 px-3 text-right">{t("revVar", "Rev. Var %")}</th>
                    <th className="py-2 px-3 text-right">{t("actExpense", "Act. Expense")}</th>
                    <th className="py-2 px-3 text-right">{t("budExpense", "Bud. Expense")}</th>
                    <th className="py-2 px-3 text-right">{t("expVar", "Exp. Var %")}</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetComparison.categories?.map((c, idx) =>
                <tr key={idx} className="border-t">
                      <td className="py-2 px-3">{c.category}</td>
                      <td className="py-2 px-3 text-right text-emerald-600">${c.actual_revenue?.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right text-blue-600">${c.budget_revenue?.toLocaleString()}</td>
                      <td className={`py-2 px-3 text-right ${c.revenue_variance_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {c.revenue_variance_pct > 0 ? '+' : ''}{c.revenue_variance_pct}%
                      </td>
                      <td className="py-2 px-3 text-right text-red-600">${c.actual_expenses?.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right text-amber-600">${c.budget_expenses?.toLocaleString()}</td>
                      <td className={`py-2 px-3 text-right ${c.expense_variance_pct <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {c.expense_variance_pct > 0 ? '+' : ''}{c.expense_variance_pct}%
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetModal(false)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Reconciliation Modal */}
      <Dialog open={showReconciliationModal} onOpenChange={setShowReconciliationModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-purple-600" />{t("bankReconciliation", "Bank Reconciliation")}

            </DialogTitle>
          </DialogHeader>
          {bankReconciliation &&
          <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("totalAccounts", "Total Accounts")}</p>
                    <p className="text-xl font-bold">{bankReconciliation.summary?.total_accounts}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("reconciled", "Reconciled")}</p>
                    <p className="text-xl font-bold text-emerald-600">{bankReconciliation.summary?.reconciled_accounts}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("unreconciledItems", "Unreconciled Items")}</p>
                    <p className="text-xl font-bold text-amber-600">{bankReconciliation.summary?.total_unreconciled_items}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("totalDifference", "Total Difference")}</p>
                    <p className={`text-xl font-bold ${bankReconciliation.summary?.total_difference === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${bankReconciliation.summary?.total_difference?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Accounts */}
              <div className="space-y-4">
                {bankReconciliation.accounts?.map((acc, idx) =>
              <Card key={idx}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium">{acc.account_name}</h4>
                          <p className="text-xs text-muted-foreground">{acc.account_type} • {acc.currency}</p>
                        </div>
                        <Badge className={acc.is_reconciled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {acc.is_reconciled ? 'Reconciled' : 'Needs Review'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">{t("bookBalance", "Book Balance")}</p>
                          <p className="font-mono font-medium">${acc.book_balance?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t("bankBalance", "Bank Balance")}</p>
                          <p className="font-mono font-medium">${acc.bank_balance?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t("difference", "Difference")}</p>
                          <p className={`font-mono font-medium ${acc.difference === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            ${acc.difference?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {acc.unreconciled_items?.length > 0 &&
                  <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium mb-2">{t("unreconciledItems", "Unreconciled Items (")}{acc.unreconciled_count})</p>
                          <div className="space-y-1">
                            {acc.unreconciled_items.map((item, iIdx) =>
                      <div key={iIdx} className="flex justify-between text-xs">
                                <span className="text-muted-foreground">{item.description}</span>
                                <span className="font-mono">${item.amount?.toLocaleString()}</span>
                              </div>
                      )}
                          </div>
                        </div>
                  }
                    </CardContent>
                  </Card>
              )}
              </div>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReconciliationModal(false)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};
